import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  PrinterOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { Button, Card, Flex, Space, Table, TableProps } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import OrderSearch from '../../lib/OrderSearch';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { notifyError, notifySuccess, priceFormat } from '../../store/utils';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { orderDownload, parseSearchParam } from '../../store/orderUtils';
import queryString from 'query-string';
import { PagedResultState } from '../../common/CommonResult';
import { API } from '../../api';
import { ParcelStatusContext } from '../../context/ParcelStatusProvider';
import ParcelOrderPrint from '../../lib/parcel/ParcelOrderPrint';
import ParcelOrderDelete from '../../lib/parcel/ParcelOrderDelete';
import { nextStatusList } from '../../common/delivery_status';
import { ReasonsStateType } from '../../common/reasons';
import ReasonsModal from '../../lib/ReasonsModal';
import { LineItemScanType } from '../../common/lineItem';
import ParcelOrderDeliveryMan from '../../lib/parcel/ParcelOrderDeliveryMan';
import { UserContext } from '../../context/UserProvider';

const OrderList = () => {
  const { getCustomer } = useContext(UserContext);
  const navigate = useNavigate();

  const onSerach = useCallback(async (query: any) => {
    navigate(`/order/list?${queryString.stringify(query)}`);
  }, []);

  return (
    <Card
      title="Захиалгын жагсаалт"
      extra={
        <Link to="/order/new">
          <Button type="primary" icon={<PlusOutlined />}>
            Захиалга үүсгэх
          </Button>
        </Link>
      }
    >
      <Flex gap={24} vertical>
        <OrderSearch
          isCustomer={!getCustomer}
          isPrint={!getCustomer}
          onSearch={(params: any) => onSerach(params)}
        />
        <OrderView />
      </Flex>
    </Card>
  );
};
export default OrderList;

const OrderView = () => {
  const { getCustomer } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { statusList } = useContext(ParcelStatusContext);
  const [selected, setSelected] = useState<any[]>([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [print, setPrint] = useState<{ visible: boolean; orderIds: number[] }>({
    visible: false,
    orderIds: [],
  });

  const [orderDelete, setOrderDelete] = useState<{ visible: boolean; lineItemIds: number[] }>({
    visible: false,
    lineItemIds: [],
  });

  const [deliveryMan, setDeliveryMan] = useState<{ visible: boolean; parcelOrderNos: number[] }>({
    visible: false,
    parcelOrderNos: [],
  });

  const [state, setState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  const [reason, setReason] = useState<ReasonsStateType>({ visible: false });
  const [disabled, setDisabled] = useState(false);

  const batchChangeOrder = useCallback(
    async (_status: any) => {
      console.log(selected);
      let isPrint = true;
      selected.forEach((element: any) => {
        if (element.order.printed === false) {
          isPrint = false;
        }
      });
      if (!isPrint) {
        notifyError('Баримт хэвлээгүй байна');
        return;
      }

      if (_status.shipmentStatusReasons && (_status.shipmentStatusReasons || []).length) {
        setReason({
          visible: true,
          status: _status,
          barcodeItems: selected.map((it: any) => it.barcode),
        });
        return;
      }
      const payload: LineItemScanType = {
        status: _status.cd,
        barcodeItems: selected.map((it: any) => {
          return {
            barcode: it.barcode,
          };
        }),
      };
      batchLineItemUpdate(payload);
    },
    [selected]
  );

  const batchLineItemUpdate = useCallback(
    async (payload: LineItemScanType) => {
      try {
        setDisabled(true);
        const {
          result: { data },
        } = await API.post({ apiVersion: 'api' })(`/order/status`, payload);
        const message = `Нийт ${(data?.successBarcodeList || []).length + (data?.errorBarCodeList || []).length}ш илгээмж. Амжилттай ${(data?.successBarcodeList || []).length}ш. Алдаатай ${(data?.errorBarCodeList || []).length}ш`;
        notifySuccess(message);
        fetchOrdersList();
      } catch (error: any) {
        notifyError(error.message);
      } finally {
        setDisabled(false);
      }
    },
    [location]
  );

  const fetchOrdersList = useCallback(async () => {
    setState({
      loading: true,
      result: state.result || {},
    });
    try {
      const { result } = await API.get({ apiVersion: 'api' })(
        `/order/list${location.search}&status=NEW`
      );
      setState({
        loading: false,
        result,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [location, state, selected]);

  const download = useCallback(async () => {
    setDownloadLoading(true);
    await orderDownload(location.search, 'NEW');
    setDownloadLoading(false);
  }, [location]);

  const batchPrintOrder = useCallback(() => {
    try {
      setPrintLoading(true);
      const orderIds: number[] = [...new Set(selected.map((itm) => itm.order.no))];
      setPrint({
        visible: true,
        orderIds: orderIds,
      });
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setPrintLoading(false);
    }
  }, [selected, location]);

  const batchFirstDeliveryManChange = useCallback(() => {
    try {
      const parcelOrderNos: number[] = [...new Set(selected.map((itm) => itm.order.no))];
      setDeliveryMan({
        visible: true,
        parcelOrderNos,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [selected]);

  const batchPrintDelete = useCallback(() => {
    const lineItemIds: number[] = selected.map((itm) => itm.no);
    setOrderDelete({
      visible: true,
      lineItemIds: lineItemIds,
    });
  }, [selected]);

  useEffect(() => {
    if (location.search) {
      fetchOrdersList();
    }
  }, [location]);

  useEffect(() => {
    if (!location.search) {
      const params = parseSearchParam({});
      navigate(`/order/list?${queryString.stringify(params)}`);
    }
  }, [location]);

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      const params = queryString.parse(location.search);
      const search = {
        ...params,
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      };
      navigate(`/order/list?${queryString.stringify(search)}`);
    },
    [location, navigate]
  );

  const columns: ColumnsType<any> = [
    {
      title: '#',
      key: 'shipmentOrderNo',
      width: 100,
      fixed: 'left',
      render: (_val: number, row: any) => (
        <Link to={`/order/detail/${row.order?.no}`}>#{row.order?.no}</Link>
      ),
    },
    {
      title: 'Огноо',
      width: 100,
      fixed: 'left',
      render: (_val: string, row: any) => (
        <span>{dayjs(row.createdAt).format('YYYY-MM-DD HH:mm')}</span>
      ),
    },
    {
      title: 'ИА.Салбар',
      width: 120,
      render: (_val: any, row: any) => <span>{row?.order?.firstBranch?.name}</span>,
    },
    {
      title: 'ИА.Хүргэгч',
      width: 150,
      render: (_val: any, row: any) => <span>{row?.order?.firstDeliveryMan?.firstName}</span>,
    },
    {
      title: 'Илгээгч',
      render: (_val: any, row: any) => (
        <div>
          <span style={{ display: 'block' }}>
            {row?.order?.senderFname + ' ' + row?.order?.senderPhone1}
          </span>
          <span>
            {row?.order?.senderProvinceName} {row?.order?.senderDistrictName}{' '}
            {row?.order?.senderAddressDetail}
          </span>
        </div>
      ),
    },
    {
      title: 'Хүргэлт',
      width: 80,
      render: (_val: any, row: any) => <span>{row.order?.deliveryType?.text}</span>,
    },
    {
      title: 'Хэмжээ',
      dataIndex: 'weightType',
      width: 80,
      render: (val: any) => <span>{val?.name}</span>,
    },
    {
      title: 'Дүн',
      dataIndex: 'price',
      width: 80,
      render: (val: number) => <span>{priceFormat(val)}₮</span>,
    },
    {
      title: 'Суваг',
      width: 100,
      render: (_val: any, row: any) => <span>{row?.order?.channelType?.text} </span>,
    },
    {
      title: 'Харилцагч',
      width: 100,
      render: (_val: any, row: any) => <span>{row.order?.customer?.name} </span>,
    },
    {
      title: 'Баримт ',
      width: 130,
      render: (_val: any, row: any) => <div>{row?.barcode}</div>,
    },
    {
      title: 'Үйлдэл',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_val: any, row: any) => (
        <div>
          <Button
            size="small"
            type="default"
            icon={<EditOutlined />}
            onClick={() => navigate(`/order/update/${row.order?.no}`)}
          />
        </div>
      ),
    },
  ];

  const selectedRowKeys = useMemo(() => {
    return selected.map((itm) => itm.no);
  }, [selected]);

  const rowSelection: TableProps<any>['rowSelection'] = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
      console.log(selected);
      setSelected(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: any) => ({
      disabled: record.name === 'Disabled User',
      name: record.name,
    }),
    preserveSelectedRowKeys: true,
  };

  const nextStatus = useMemo(() => {
    return nextStatusList(statusList, 'NEW');
  }, [statusList]);

  return (
    <Fragment>
      <Flex vertical gap={24}>
        <Flex gap={24} justify="start">
          <div>
            <Space.Compact block>
              {!getCustomer ? (
                <Fragment>
                  {(nextStatus || []).map((item: any) => {
                    return (
                      <Button
                        type="default"
                        onClick={() => batchChangeOrder(item)}
                        disabled={!(selected.length > 0) || disabled}
                      >
                        {item.scanName} {selected.length > 0 ? ' (' + selected.length + ')' : ''}
                      </Button>
                    );
                  })}
                </Fragment>
              ) : null}
              <Button
                type="default"
                icon={<PrinterOutlined />}
                onClick={() => batchPrintOrder()}
                disabled={!(selected.length > 0)}
                loading={printLoading}
              >
                Баримт хэвлэх {selected.length > 0 ? ' (' + selected.length + ')' : ''}
              </Button>
              {!getCustomer ? (
                <Button
                  type="default"
                  icon={<UserSwitchOutlined />}
                  onClick={() => batchFirstDeliveryManChange()}
                  disabled={!(selected.length > 0)}
                  loading={printLoading}
                >
                  ИА.Хүргэгч солих{selected.length > 0 ? ' (' + selected.length + ')' : ''}
                </Button>
              ) : null}
              {!getCustomer ? (
                <Button type="default" loading={downloadLoading} onClick={() => download()}>
                  Эксел татах
                </Button>
              ) : null}
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => batchPrintDelete()}
                disabled={!(selected.length > 0)}
              >
                Баримт устгах {selected.length > 0 ? ' (' + selected.length + ')' : ''}
              </Button>
            </Space.Compact>
          </div>
        </Flex>
        <Flex>
          <div>
            Нийт тоо: <b>{state?.result?.total}ш</b> Нийт дүн:{' '}
            <b>{priceFormat(state?.result?.amount || 0)}₮</b>
          </div>
        </Flex>
      </Flex>
      <Table
        rowKey={(record: any) => record.no}
        size="small"
        scroll={{ x: 1340 }}
        loading={state.loading}
        columns={columns}
        rowSelection={rowSelection}
        pagination={{
          current: state?.result?.page || 1,
          size: 'small',
          pageSize: state?.result?.size || 10,
          total: state?.result?.total,
          position: ['bottomRight', 'topRight'],
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        dataSource={state.result.data || []}
      />
      <ParcelOrderPrint
        visible={print.visible}
        orderIds={print.orderIds}
        onClose={() => {
          setPrint({ visible: false, orderIds: [] });
          setSelected([]);
        }}
        onReload={() => fetchOrdersList()}
      />
      <ParcelOrderDelete
        visible={orderDelete.visible}
        lineItemIds={orderDelete.lineItemIds}
        onClose={() => {
          setOrderDelete({ visible: false, lineItemIds: [] });
          setSelected([]);
        }}
        onReload={() => fetchOrdersList()}
      />
      <ReasonsModal
        visible={reason.visible}
        status={reason.status}
        barcodeItems={reason.barcodeItems}
        onClose={() => setReason({ visible: false })}
        onFinish={(payload: LineItemScanType) => {
          setReason({ visible: false });
          batchLineItemUpdate(payload);
        }}
      />
      <ParcelOrderDeliveryMan
        visible={deliveryMan.visible}
        parcelOrderNos={deliveryMan.parcelOrderNos}
        onClose={() => {
          setDeliveryMan({ visible: false, parcelOrderNos: [] });
          setSelected([]);
        }}
        onReload={() => fetchOrdersList()}
      />
    </Fragment>
  );
};
